import { Fragment } from 'react';
import emojiFlags from 'emoji-flags';
import { INK } from '../Landing/ui';

function flagSrc(countryCode) {
  if (!countryCode || !emojiFlags.countryCode(countryCode.toUpperCase()))
    return null;
  return `/flags/${countryCode.toLowerCase()}.svg`;
}

function Dots({ level }) {
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-align-middle">
      {[1, 2, 3, 4, 5].map(i => (
        <span
          key={i}
          className={`tw-h-1.5 tw-w-1.5 tw-rounded-full ${
            i <= level
              ? 'tw-bg-primary-600 dark:tw-bg-primary-300'
              : 'tw-bg-[#E7E0CF] dark:tw-bg-[#26382F]'
          }`}
        />
      ))}
    </span>
  );
}

function englishProficiencyDescription(englishProficiency) {
  let res = 'Moderate';
  if (englishProficiency === 5) res = 'Very High';
  if (englishProficiency === 4) res = 'High';
  if (englishProficiency === 2) res = 'Low';
  if (englishProficiency === 1) res = 'Very Low';
  return res;
}

// Quick facts as a passport entry card — mono field labels, a flag in the
// corner and a machine-readable zone at the bottom for good measure.
export default function EntryCard({ locationDetails }) {
  const {
    countryCode,
    currencies,
    languages,
    englishProficiency,
    capitalCity,
    population,
    drivingSide,
    plugTypes,
    timeZones,
  } = locationDetails;
  const flag = flagSrc(countryCode);

  const fields = [];
  if (currencies && currencies.length > 0) {
    fields.push({
      label: currencies.length === 1 ? 'Currency' : 'Currencies',
      value: currencies
        .map(currency => `${currency.name} (${currency.symbol})`)
        .join(', '),
    });
    if (currencies[0].isoCurrencyCode !== 'USD')
      fields.push({
        label: 'Exchange rate',
        value: currencies
          .filter(currency => currency.isoCurrencyCode !== 'USD')
          .map(
            currency =>
              `US$1 = ${currency.symbol}${currency.dollarExchangeRate}`,
          )
          .join(', '),
      });
  }
  if (languages && languages.length > 0)
    fields.push({
      label: languages.length === 1 ? 'Language' : 'Languages',
      value: languages.join(', '),
    });
  if (languages && languages.indexOf('English') === -1 && englishProficiency)
    fields.push({
      label: 'English',
      value: (
        <span className="tw-inline-flex tw-items-center tw-gap-2">
          <Dots level={englishProficiency} />
          {englishProficiencyDescription(englishProficiency)}
        </span>
      ),
    });
  if (capitalCity) fields.push({ label: 'Capital', value: capitalCity });
  if (population)
    fields.push({ label: 'Population', value: `${population} million` });
  if (drivingSide) fields.push({ label: 'Drives on', value: drivingSide });
  if (plugTypes && plugTypes.length > 0)
    fields.push({ label: 'Plugs', value: plugTypes.join(', ') });
  if (timeZones && timeZones.length > 0)
    fields.push({
      label: timeZones.length === 1 ? 'Time zone' : 'Time zones',
      value: timeZones.map((timeZone, i) => (
        <Fragment key={timeZone.utcOffset}>
          {i > 0 ? ', ' : ''}
          {timeZone.timeZoneName} (UTC{timeZone.utcOffset})
        </Fragment>
      )),
    });

  return (
    <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-p-6 sm:tw-p-7 tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)]">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
        <p className="tw-m-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-primary-700 dark:tw-text-primary-300">
          Entry card · {countryCode ? countryCode.toUpperCase() : ''}
        </p>
        {flag && (
          <img
            src={flag}
            alt=""
            aria-hidden="true"
            width={20}
            height={20}
            loading="lazy"
            className="tw-h-5 tw-w-5"
          />
        )}
      </div>

      <dl className="tw-m-0 tw-mt-5 tw-grid tw-grid-cols-2 tw-gap-x-5 tw-gap-y-4">
        {fields.map(field => (
          <div key={field.label}>
            <dt className="tw-font-mono tw-text-[9px] tw-font-medium tw-uppercase tw-tracking-[0.25em] tw-text-[#8A958F]">
              {field.label}
            </dt>
            <dd
              className={`${INK} tw-m-0 tw-mt-1 tw-text-sm tw-font-semibold tw-leading-snug`}
            >
              {field.value}
            </dd>
          </div>
        ))}
      </dl>

      <p
        aria-hidden="true"
        className="tw-m-0 tw-mt-6 tw-overflow-hidden tw-whitespace-nowrap tw-border-0 tw-border-t tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] tw-pt-3 tw-font-mono tw-text-[10px] tw-tracking-[0.3em] tw-text-[#8A958F]"
      >
        {`P<${
          countryCode ? countryCode.toUpperCase() : 'TF'
        }<TRAVELFEED<FIELD<GUIDE<<<<<<<<<<<<<<<<<<<<`}
      </p>
    </div>
  );
}
